// src/utils/googleMapsLoader.ts
// Shared Google Maps Places API loader for all components

let googleMapsScriptPromise: Promise<boolean> | null = null;

const waitForGooglePlaces = (timeoutMs = 8000, pollMs = 100): Promise<boolean> => {
    return new Promise((resolve) => {
        const start = Date.now();
        const check = () => {
            const google: any = (window as any).google;
            if (google?.maps?.places) {
                resolve(true);
                return;
            }
            if (Date.now() - start >= timeoutMs) {
                resolve(false);
                return;
            }
            setTimeout(check, pollMs);
        };
        check();
    });
};

export const loadGoogleMapsScript = async (apiKey?: string): Promise<boolean> => {
    if (googleMapsScriptPromise) return googleMapsScriptPromise;
    googleMapsScriptPromise = new Promise<boolean>(async (resolve, reject) => {
        const existing = document.getElementById("google-maps-script") as HTMLScriptElement | null;
        if (existing) {
            const ready = await waitForGooglePlaces();
            if (ready) return resolve(true);
            existing.addEventListener("load", () => resolve(true), { once: true });
            setTimeout(() => resolve(false), 100);
            return;
        }
        const script = document.createElement("script");
        script.id = "google-maps-script";
        const key = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string) || (window as any)?.ENV?.GOOGLE_MAPS_API_KEY || apiKey;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${key || ""}&libraries=places`;
        script.async = true;
        script.defer = true;
        // Add loading="async" for best-practice loading (per Google warning)
        (script as any).loading = "async";
        script.onload = () => {
            try {
                const style = document.createElement("style");
                style.setAttribute("data-ams-pac-zindex", "true");
                style.innerHTML = `.pac-container{z-index:2005 !important;}`;
                document.head.appendChild(style);
            } catch { }
            resolve(true);
        };
        script.onerror = () => reject(new Error("Failed to load Google Maps script"));
        document.body.appendChild(script);
    });
    return googleMapsScriptPromise;
};
